/** Chrome's install-prompt event, absent from TypeScript's DOM lib. Declared
 *  once here so no call site casts. */
export interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: string }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let deferred: BeforeInstallPromptEvent | null = null;
let listening = false;
const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of listeners) listener();
}

/**
 * `beforeinstallprompt` fires ONCE, shortly after the manifest plus the worker
 * make the origin installable, and never re-fires. React has not mounted
 * anything by then — the entry gates render on persist hydration, then a token
 * refresh, then the persona probes' network round trip — so a listener attached
 * from a component effect routinely misses it, the install tier silently
 * collapses to `tab`, and install prompting is dead on the one platform that
 * has the API. Hence a module-scope holder, installed from `main.tsx` before
 * the first render.
 *
 * Calling this twice attaches nothing further; the holder is a singleton.
 */
export function captureInstallPrompt(): void {
  if (listening) return;
  listening = true;
  window.addEventListener("beforeinstallprompt", (event) => {
    // Suppresses Chrome's own mini-infobar, so the portal owns the affordance
    // and can state the durability promise honestly beside it.
    event.preventDefault();
    deferred = event;
    notify();
  });
}

export function getDeferredInstallPrompt(): BeforeInstallPromptEvent | null {
  return deferred;
}

export function clearDeferredInstallPrompt(): void {
  if (deferred === null) return;
  deferred = null;
  notify();
}

/** Notifies on capture and on clear, so a hook mounting AFTER the event still
 *  sees it and re-renders when it is consumed. */
export function subscribeInstallPrompt(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
